import React from 'react';
import { compactAmount } from './DashboardCharts';
import type { DashboardMonth } from './dashboardModel';

interface Props {
  months: DashboardMonth[];
  labels: string[];
  language: string;
  /** Opens the month in „Sprzedaż i zamówienia” (its cut of the sales). */
  onOpen: (month: string) => void;
  /** Accessible name of a bar: month, invoiced, forecast part, target. */
  describe: (month: DashboardMonth) => string;
  /** Month indexes of the page's period (a quarter or a month): the others are muted. */
  highlight?: Set<number>;
}

const WIDTH = 1080;
const HEIGHT = 200;
const TOP = 22;
const BOTTOM = 26;
const LEFT = 56;

/** Settled revenue, the planned rest (scenario-scaled) and the target of one month. */
export function monthBarParts(month: DashboardMonth): { settled: number; planned: number; target: number | null } {
  return {
    settled: month.actualRevenue,
    planned: Math.max(0, month.forecastRevenue - month.actualRevenue),
    target: month.revenueTarget,
  };
}

// „Sprzedaż miesiącami” on „Pulpit zarządu”: one bar per month of the year,
// the invoiced part solid and the forecast rest light (same marks as the
// trend of „Przekrój sprzedaży”), the revenue target as a tick. A bar is a
// button: it opens that month's cut in „Sprzedaż i zamówienia”.
const MonthlySalesBars: React.FC<Props> = ({ months, labels, language, onOpen, describe, highlight }) => {
  const parts = months.map(monthBarParts);
  const max = Math.max(1, ...parts.map((part) => Math.max(part.settled + part.planned, part.target ?? 0)));
  const plot = HEIGHT - TOP - BOTTOM;
  const y = (value: number) => TOP + ((max - value) / max) * plot;
  const slot = (WIDTH - LEFT) / Math.max(1, months.length);
  const barWidth = Math.min(44, slot * 0.56);

  return (
    <svg className="mgmt-month-bars" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="group">
      {[max, max / 2, 0].map((value) => (
        <g key={`grid-${value}`}>
          <line className="mgmt-month-bars__grid" x1={LEFT} x2={WIDTH} y1={y(value)} y2={y(value)} />
          <text className="mgmt-month-bars__axis" x={LEFT - 8} y={y(value) + 4} textAnchor="end">{compactAmount(value, language)}</text>
        </g>
      ))}
      {months.map((month, index) => {
        const part = parts[index];
        const x = LEFT + index * slot + (slot - barWidth) / 2;
        const total = part.settled + part.planned;
        const open = () => onOpen(month.month);
        return (
          <g
            key={month.month}
            className={`mgmt-month-bars__month${highlight && !highlight.has(month.index) ? ' is-muted' : ''}${highlight?.has(month.index) ? ' is-period' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={describe(month)}
            onClick={open}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); } }}
          >
            <title>{describe(month)}</title>
            <rect className="mgmt-month-bars__hit" x={LEFT + index * slot} y={TOP - 18} width={slot} height={plot + 18 + BOTTOM} />
            {part.settled > 0 && (
              <rect className="mgmt-month-bars__bar is-settled" x={x} y={y(part.settled)} width={barWidth} height={y(0) - y(part.settled)} rx={3} />
            )}
            {part.planned > 0 && (
              <rect className="mgmt-month-bars__bar is-forecast" x={x} y={y(total)} width={barWidth} height={y(part.settled) - y(total)} rx={3} />
            )}
            {part.target != null && part.target > 0 && (
              <line className="mgmt-month-bars__target" x1={x - 6} x2={x + barWidth + 6} y1={y(part.target)} y2={y(part.target)} />
            )}
            {total > 0 && (
              <text className="mgmt-month-bars__value" x={x + barWidth / 2} y={y(total) - 6} textAnchor="middle">{compactAmount(total, language)}</text>
            )}
            <text className="mgmt-month-bars__axis" x={x + barWidth / 2} y={HEIGHT - 8} textAnchor="middle">{labels[index]}</text>
          </g>
        );
      })}
    </svg>
  );
};

export default MonthlySalesBars;
